import { useAuth0 } from "@auth0/auth0-react";
import { useContext, useEffect, useState } from "react";
import request, { RequestDocument } from "graphql-request";
import { useMutation, useQuery, useInfiniteQuery } from "react-query";
import { IdToken } from "@auth0/auth0-spa-js";
import * as Sentry from "@sentry/react";
import { FeatureAccessContext } from "../context/featureAccess";
import { useTrace } from "../context/trace";

export const useGraphQLAuthorizedQuery = <DataType = any>({
  queryKey,
  url,
  document,
  variables,
  enabled,
  cacheTime = 5 * 60 * 1000,
}: {
  queryKey: any[];
  url: string;
  document: RequestDocument;
  variables: { [key: string]: any };
  enabled: boolean;
  cacheTime?: number;
}) => {
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { createTraceId, getCurrentSpanId, logTraceEvent } = useTrace();

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  return useQuery<DataType>({
    queryKey,
    queryFn: async () => {
      return Sentry.startSpan(
        {
          name: `GraphQL ${url}`,
          op: "http.client",
        },
        async (span) => {
          const spanContext = span.spanContext();
          const traceId = createTraceId();
          const spanId = spanContext.spanId || getCurrentSpanId();
          const sessionId = sessionStorage.getItem("sessionId") || "";

          const traceIdW3CFormat = `00-${traceId.padStart(32, '0').substring(0, 32)}-${spanId?.padStart(16, '0').substring(0, 16)}-01`;

          const headers = {
            "Content-Type": "application/json",
            authorization: tokenId?.__raw ? tokenId.__raw : "",
            "x-trace-id": traceIdW3CFormat,
            "x-session-id": sessionId,
          };
          const traceIdFromHeader = traceIdW3CFormat.split("-")[1];
          Sentry.setTag("apiTraceId", traceIdFromHeader);
          Sentry.setTag("sessionId", sessionId);
          Sentry.setContext("request", {
            url,
            traceId,
            spanId,
            sessionId,
          });

          logTraceEvent({
            traceId,
            event: "graphql_request",
            level: "info",
            message: `GraphQL query to ${url}`,
            metadata: { url, queryKey, spanId },
          });

          try {
            const result = await request<DataType>(url, document, variables, headers);
            return result;
          } catch (error: any) {
            throw error;
          }
        }
      );
    },
    enabled: enabled && isAuthenticated && !!tokenId,
    cacheTime: cacheTime,
  });
};

type TRequestOptions = {
  method: string;
  headers: Record<string, string>;
  body?: any;
};

export const useAuthorizedMutation = <
  DataType = any,
  VariablesType = Record<string, any>
>({
  url,
  method = "POST",
  onSuccess,
}: {
  url: string;
  method?: string;
  onSuccess?: (
    data: DataType,
    variables: VariablesType,
    context: unknown
  ) => Promise<unknown> | void;
}) => {
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { createTraceId, getCurrentSpanId, logTraceEvent } = useTrace();

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  return useMutation<DataType, unknown, VariablesType>({
    mutationFn: async (data: VariablesType | null) => {
      if (!data) {
        throw new Error("useMutation: no data supplied");
      }

      return Sentry.startSpan(
        {
          name: `${method} ${url}`,
          op: "http.client",
        },
        async (span) => {
          const spanContext = span.spanContext();
          const traceId = createTraceId();
          const spanId = spanContext.spanId || getCurrentSpanId();
          const sessionId = sessionStorage.getItem("sessionId") || "";

          const traceIdW3CFormat = `00-${traceId.padStart(32, '0').substring(0, 32)}-${spanId?.padStart(16, '0').substring(0, 16)}-01`;
          const traceIdFromHeader = traceIdW3CFormat.split("-")[1];
          Sentry.setTag("apiTraceId", traceIdFromHeader);
          Sentry.setTag("sessionId", sessionId);
          Sentry.setContext("request", {
            url,
            method,
            traceId,
            spanId,
            sessionId,
          });


  // Fetch token directly if not available in state (handles race condition)
          let authToken = tokenId?.__raw ?? "";
          if (!authToken && isAuthenticated) {
            const freshToken = await getIdTokenClaims();
            if (freshToken?.__raw) {
              authToken = freshToken.__raw;
              setToken(freshToken);
            }
          }




          let headers: Record<string, string> = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "x-trace-id": traceIdW3CFormat,
            "x-session-id": sessionId,
          };
          if (selectedWorkspace) {
            headers["x-tenant-id"] = selectedWorkspace;
          }

          logTraceEvent({
            traceId,
            event: "api_mutation_request",
            level: "info",
            message: `${method} ${url}`,
            metadata: { method, url, spanId },
          });

          const options: TRequestOptions = {
            method: method,
            headers,
          };
          if (method !== "GET") {
            options.body = JSON.stringify(data);
          }

          try {
            const res = await fetch(url, options);
            let result;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              result = await res.json();
            } else {
              result = await res.text();
            }

            if (res.ok) {
              return result;
            }

            const responseError = {
              type: "Error",
              message: result.message || "Something went wrong",
              code: result.statusCode,
            };

            let error = new Error();
            error = { ...error, ...responseError };

            throw error;
          } catch (error: any) {
            throw error;
          }
        }
      );
    },
    onSuccess,
  });
};

export const useAuthorizedQuery = <
  DataType = any,
  VariablesType = Record<string, any>
>({
  url,
  queryKey,
  enabled,
  method = "GET",
  cacheTime = 5 * 60 * 1000,
  refetchInterval,
  body,
}: {
  url: string;
  queryKey: string;
  enabled: boolean;
  method?: string;
  cacheTime?: number;
  refetchInterval?: number;
  body?: VariablesType;
}) => {
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { createTraceId, getCurrentSpanId, logTraceEvent } = useTrace();

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  return useQuery<DataType>({
    queryKey: `${queryKey}-${selectedWorkspace}`,
    queryFn: async (data: Record<string, any> | null) => {
      if (!data) {
        throw new Error("useQuery: no data supplied");
      }

      return Sentry.startSpan(
        {
          name: `${method} ${url}`,
          op: "http.client",
        },
        async (span) => {
          const spanContext = span.spanContext();
          const traceId = spanContext.traceId || createTraceId();
          const spanId = spanContext.spanId || getCurrentSpanId();
          const sessionId = sessionStorage.getItem("sessionId") || "";

          const traceIdW3CFormat = `00-${traceId.padStart(32, '0').substring(0, 32)}-${spanId?.padStart(16, '0').substring(0, 16)}-01`;
          const traceIdFromHeader = traceIdW3CFormat.split("-")[1];
          Sentry.setTag("apiTraceId", traceIdFromHeader);
          Sentry.setTag("sessionId", sessionId);
          Sentry.setContext("request", {
            url,
            method,
            traceId,
            spanId,
            sessionId,
          });

          const authToken = tokenId?.__raw ? tokenId.__raw : "";
          let headers: Record<string, string> = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "x-trace-id": traceIdW3CFormat,
            "x-session-id": sessionId,
          };
          if (selectedWorkspace) {
            headers["x-tenant-id"] = selectedWorkspace;
          }

          logTraceEvent({
            traceId,
            event: "api_query_request",
            level: "info",
            message: `${method} ${url}`,
            metadata: { method, url, queryKey, spanId },
          });

          const options: TRequestOptions = {
            method: method,
            headers,
          };
          if (method !== "GET" && body != null) {
            options.body = JSON.stringify(body);
          }

          try {
            const res = await fetch(url, options);
            let result;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              result = await res.json();
            } else {
              result = await res.text();
            }

            if (res.ok) {
              return result;
            }

            const responseError = {
              type: "Error",
              message: result.message || "Something went wrong",
              code: result.statusCode,
            };

            return responseError;
          } catch (error: any) {
            throw error;
          }
        }
      );
    },
    enabled: enabled && isAuthenticated && !!tokenId,
    cacheTime: cacheTime,
    refetchInterval,
  });
};

export const useAuthorizedInfiniteQuery = <DataType = any>({
  url,
  queryKey,
  enabled,
  method = "GET",
  cacheTime = 5 * 60 * 1000,
  body = {},
  pageKey = "page",
  getNextPageParam,
}: {
  url: string;
  queryKey: string;
  enabled: boolean;
  method?: string;
  cacheTime?: number;
  body?: any;
  pageKey?: string;
  getNextPageParam?: (lastPage: DataType, allPages: DataType[]) => unknown;
}) => {
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { createTraceId, getCurrentSpanId, logTraceEvent } = useTrace();

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  return useInfiniteQuery<DataType>({
    queryKey: `${queryKey}-${selectedWorkspace}`,
    queryFn: async ({ pageParam = 1 }) => {
      return Sentry.startSpan(
        {
          name: `${method} ${url} (page: ${pageParam})`,
          op: "http.client",
        },
        async (span) => {
          const spanContext = span.spanContext();
          const traceId = createTraceId();
          const spanId = spanContext.spanId || getCurrentSpanId();
          const sessionId = sessionStorage.getItem("sessionId") || "";

          const traceIdW3CFormat = `00-${traceId.padStart(32, '0').substring(0, 32)}-${spanId?.padStart(16, '0').substring(0, 16)}-01`;
          const traceIdFromHeader = traceIdW3CFormat.split("-")[1];
          Sentry.setTag("apiTraceId", traceIdFromHeader);
          Sentry.setTag("sessionId", sessionId);
          Sentry.setContext("request", {
            url,
            method,
            traceId,
            spanId,
            sessionId,
          });

          const authToken = tokenId?.__raw ? tokenId.__raw : "";
          let headers: Record<string, string> = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "x-trace-id": traceIdW3CFormat,
            "x-session-id": sessionId,
          };
          if (selectedWorkspace) {
            headers["x-tenant-id"] = selectedWorkspace;
          }

          logTraceEvent({
            traceId,
            event: "api_infinite_query_request",
            level: "info",
            message: `${method} ${url} (page: ${pageParam})`,
            metadata: { method, url, pageParam, queryKey, spanId },
          });

          const options: TRequestOptions = {
            method: method,
            headers,
          };
          if (method !== "GET") {
            options.body = JSON.stringify({ [pageKey]: pageParam, ...body });
          }

          try {
            const res = await fetch(url, options);
            const result = await res.json();

            return result;
          } catch (error: any) {
            throw error;
          }
        }
      );
    },
    enabled: enabled && isAuthenticated && !!tokenId,
    cacheTime: cacheTime,
    getNextPageParam,
  });
};
